// Campaigns → «Newsletter» (ideas track 6 + 23). The audience «Συνδρομητές newsletter» is the website's confirmed
// subscribers (double opt-in); the CRM sees only counts — the addresses stay sealed on the platform, which sends through
// its mailer (Brevo) with an unsubscribe link and List-Unsubscribe headers. Change alerts prepared after a guide update
// arrive as drafts: nothing goes out until the advisor opens one, checks both languages, sends a test and presses send.
import { useCallback, useEffect, useState } from 'react';
import { alertCampaignInput, isNewsletterCampaign, newsletterAudienceOf, newsletterProblems, recordNewsletterSend, saveNewsletterCampaign, type Campaign, type NewsletterAudience, type NewsletterMessage } from '@vero/domain';
import type { NewsletterOverview } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, Field, PageHead, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';

type TaEv = { target: HTMLTextAreaElement };
type InEv = { target: HTMLInputElement };

function useOverview() {
  const a = useAccount();
  const [data, setData] = useState<NewsletterOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    const api = accountService()?.api;
    if (!api || !a?.signedIn) { setErr('signed_out'); return; }
    try { setData(await api.newsletter()); setErr(null); } catch (e) { setErr(String((e as Error).message)); }
  }, [a?.signedIn]);
  useEffect(() => { void load(); }, [load]);
  return { data, err, reload: load };
}

/** the «Newsletter» tab: audience counts, alert drafts waiting for approval, newsletter campaigns */
export function NewsletterTab() {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const { data, err, reload } = useOverview();
  const campaigns = view.rows('campaigns').filter(isNewsletterCampaign).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const create = async (list: NewsletterAudience, input?: ReturnType<typeof alertCampaignInput>) => {
    const lang = rt.locale.startsWith('el') ? 'el' : 'en';
    const msg: NewsletterMessage = { subject: { el: '', en: '' }, body: { el: t('newsletter.body_start_el'), en: t('newsletter.body_start_en') } };
    const cs = await rt.run((v, c) => saveNewsletterCampaign(v, input ?? { title: t(`newsletter.new_title.${list}`), list, message: msg }, c), 'campaigns.created');
    if (cs) navigate(`/campaigns/${cs.entityId}`);
    void lang;
  };
  const dismiss = async (id: string) => { try { await accountService()?.api?.newsletterDismiss(id); rt.toast(t('newsletter.dismissed'), 'info'); void reload(); } catch (e) { rt.toast(String((e as Error).message), 'error'); } };
  return (
    <div data-testid="newsletter-tab">
      <section className="card card-pad">
        <div className="section-title">{t('newsletter.audience')}</div>
        {err === 'signed_out' ? <p className="small muted">{t('newsletter.need_account')}</p>
          : err ? <p className="small muted">{t('newsletter.unavailable', { code: err })}</p>
          : !data ? <p className="small muted">{t('common.loading')}</p>
          : <div className="kv" data-testid="newsletter-counts">
            <div><span>{t('newsletter.active')}</span><b>{data.counts.active}</b></div>
            <div><span>{t('newsletter.list.monthly')}</span>{data.counts.monthly}</div>
            <div><span>{t('newsletter.list.alerts')}</span>{data.counts.alerts}</div>
            <div><span>{t('newsletter.langs')}</span>{`EL ${data.counts.el} · EN ${data.counts.en}`}</div>
          </div>}
        <p className="tiny muted">{t('newsletter.privacy_note')}</p>
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn btn-primary" onClick={() => void create('monthly')} data-testid="newsletter-new-monthly"><Icon name="plus" />{t('newsletter.new.monthly')}</button>
          <button className="btn" onClick={() => void create('alerts')}><Icon name="plus" />{t('newsletter.new.alerts')}</button>
        </div>
      </section>
      {data && data.drafts.length > 0 && <section className="card card-pad" style={{ marginTop: 12 }} data-testid="newsletter-drafts">
        <div className="section-title">{t('newsletter.drafts')}</div>
        <p className="small muted">{t('newsletter.drafts_hint')}</p>
        <div className="agenda-list">{data.drafts.map((d) => (
          <div key={d.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
            <div><b>{d.title.el}</b><div className="tiny muted">{i18n.date(d.updatedAt.slice(0, 10))} · <a href={d.url.el} target="_blank" rel="noopener noreferrer">{d.url.el.replace(/^https:\/\//, '')}</a></div></div>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn btn-sm btn-primary" onClick={() => void create('alerts', alertCampaignInput(d, rt.locale.startsWith('el') ? 'el' : 'en'))}>{t('newsletter.open_draft')}</button>
              <button className="btn btn-sm btn-ghost" onClick={() => void dismiss(d.id)}>{t('newsletter.dismiss')}</button>
            </div>
          </div>))}</div>
      </section>}
      <section className="card" style={{ marginTop: 12 }}>
        {campaigns.length === 0 ? <Empty icon="mail">{t('newsletter.empty')}</Empty> : <div className="agenda-list">{campaigns.map((c) => (
          <div key={c.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} onClick={() => navigate(`/campaigns/${c.id}`)} role="button" tabIndex={0}>
            <div><b>{c.title}</b><div className="tiny muted">{t(`newsletter.list.${newsletterAudienceOf(c) ?? 'monthly'}`)}</div></div>
            <span className={`chip ${c.status === 'active' ? 'ok' : 'gold'}`}>{t(`campaigns.status.${c.status}`)}</span>
          </div>))}</div>}
      </section>
    </div>
  );
}

/** a newsletter campaign: edit both languages, send a test to yourself, then send to the list (explicit, confirmed) */
export function NewsletterCampaignDetail({ c }: { c: Campaign }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const list = newsletterAudienceOf(c) ?? 'monthly';
  const saved = (c.message ?? { subject: { el: '', en: '' }, body: { el: '', en: '' } }) as NewsletterMessage;
  const [msg, setMsg] = useState<NewsletterMessage>(saved);
  const [lang, setLang] = useState<'el' | 'en'>('el');
  const [busy, setBusy] = useState(false);
  const { data } = useOverview();
  const sends = ((c.audience as { sends?: { at: string; sent: number; failed: number; test: boolean }[] } | undefined)?.sends ?? []);
  const problems = newsletterProblems(msg);
  const dirty = JSON.stringify(msg) !== JSON.stringify(saved);
  const save = () => rt.run((v, x) => saveNewsletterCampaign(v, { id: c.id, title: c.title, list, message: msg }, x), 'common.saved');
  const send = async (test: boolean) => {
    const api = accountService()?.api;
    if (!api) { rt.toast(t('newsletter.need_account'), 'error'); return; }
    if (dirty) await save();
    if (!test && !window.confirm(t('newsletter.confirm_send', { n: data ? data.counts[list] : '?' }))) return;
    setBusy(true);
    try {
      const alertId = (c.audience as { alertId?: string | null } | undefined)?.alertId ?? null;
      const r = await api.newsletterSend({ campaignId: c.id, list, subject: msg.subject, body: msg.body, test, lang, alertId });
      await rt.run((v, x) => recordNewsletterSend(v, c.id, { at: new Date().toISOString(), sent: r.sent ?? (test ? 1 : 0), failed: r.failed ?? 0, test }, x));
      rt.toast(test ? t('newsletter.test_sent', { to: r.to ?? '' }) : t('newsletter.sent', { n: r.sent ?? 0, failed: r.failed ?? 0 }), 'success');
    } catch (e) { rt.toast(t('newsletter.send_failed', { code: String((e as Error).message) }), 'error'); }
    finally { setBusy(false); }
  };
  const set = (part: 'subject' | 'body', v: string) => setMsg((m) => ({ ...m, [part]: { ...m[part], [lang]: v } }));
  return (
    <div data-testid="newsletter-detail">
      <PageHead title={c.title} subtitle={`${t('newsletter.audience_name')} · ${t(`newsletter.list.${list}`)}${data ? ` · ${data.counts[list]}` : ''}`} actions={<button className="btn btn-ghost" onClick={() => navigate('/campaigns')}><Icon name="back" />{t('campaigns.title')}</button>} />
      <section className="card card-pad">
        <Field label={t('newsletter.language')} group><Seg values={['el', 'en'] as const} value={lang} onChange={setLang} label={(x) => (x === 'el' ? 'Ελληνικά' : 'English')} /></Field>
        <Field label={t('templates.subject')}><input className="input" value={msg.subject[lang]} maxLength={190} onChange={(e: InEv) => set('subject', e.target.value)} data-testid="newsletter-subject" /></Field>
        <Field label={t('newsletter.body')} hint={t('newsletter.body_hint')}><textarea className="input" rows={12} value={msg.body[lang]} onChange={(e: TaEv) => set('body', e.target.value)} data-testid="newsletter-body" /></Field>
        <p className="tiny muted">{t('newsletter.footer_note')}</p>
        {problems.length > 0 && <p className="small" data-testid="newsletter-problems">{problems.map((p) => t(`newsletter.problem.${p}`)).join(' · ')}</p>}
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn" disabled={!dirty || busy} onClick={() => void save()}>{t('common.save')}</button>
          <button className="btn" disabled={busy || problems.length > 0} onClick={() => void send(true)} data-testid="newsletter-test">{t('newsletter.send_test')}</button>
          <span className="spacer" />
          <button className="btn btn-primary" disabled={busy || problems.length > 0 || !sends.some((s) => s.test)} onClick={() => void send(false)} data-testid="newsletter-send">{t('newsletter.send_list')}</button>
        </div>
        {!sends.some((s) => s.test) && <p className="tiny muted">{t('newsletter.test_first')}</p>}
      </section>
      {sends.length > 0 && <section className="card card-pad" style={{ marginTop: 12 }}>
        <div className="section-title">{t('newsletter.history')}</div>
        {sends.slice().reverse().map((s, i) => <div key={i} className="small">{i18n.date(s.at.slice(0, 10))} · {s.test ? t('newsletter.test') : t('newsletter.sent_line', { n: s.sent, failed: s.failed })}</div>)}
      </section>}
    </div>
  );
}
